import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	data,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	AppContainerGroup,
	AppContainerGroupColumn,
} from '#app/components/app-container.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
	ErrorList,
	Field,
	StaySignedInLabel,
	SwitchField,
} from '#app/components/forms.tsx'
import { AuthAppLayout } from '#app/components/layout/app.js'
import { Spacer } from '#app/components/spacer.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { PasswordSchema, EmailSchema } from '#app/models/user._._schema'
import { useIsPending } from '#app/utils/misc.tsx'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	const { requireAnonymous } = await import('#app/utils/auth.server.ts')
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: ActionFunctionArgs) {
	const { requireAnonymous, login } = await import('#app/utils/auth.server.ts')
	const { checkHoneypot } = await import('#app/utils/honeypot.server.ts')
	const { handleNewSession } = await import('./login.server.ts')
	
	await requireAnonymous(request)
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid email or password',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo, remember: true },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<AuthAppLayout>
			<AppContainerGroup className="text-center">
				<h1 className="text-h1">Welcome back!</h1>
				<p className="text-body-md text-muted-foreground">
					Please enter your details.
				</p>
			</AppContainerGroup>
			<Spacer size="xs" />
			<AppContainerGroup>
				<AppContainerGroupColumn>
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<Field
							labelProps={{ children: 'Email' }}
							inputProps={{
								...getInputProps(fields.email, { type: 'email' }),
								autoFocus: true,
								autoComplete: 'email',
							}}
							errors={fields.email.errors}
						/>

						<Field
							labelProps={{ children: 'Password' }}
							inputProps={{
								...getInputProps(fields.password, {
									type: 'password',
								}),
								autoComplete: 'current-password',
							}}
							errors={fields.password.errors}
						/>

						<div className="flex flex-col gap-2">
							<SwitchField
								labelProps={{
									htmlFor: fields.remember.id,
									children: <StaySignedInLabel />,
								}}
								buttonProps={getInputProps(fields.remember, {
									type: 'checkbox',
								})}
								errors={fields.remember.errors}
							/>
						</div>

						<div className="flex justify-between">
							<div>
								<Link
									to="/forgot-password"
									className="text-body-xs font-semibold text-link"
								>
									Forgot password?
								</Link>
							</div>
						</div>

						<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
						<ErrorList errors={form.errors} id={form.errorId} />

						<div className="flex items-center justify-between gap-6 pt-3">
							<StatusButton
								className="w-full"
								status={isPending ? 'pending' : (form.status ?? 'idle')}
								type="submit"
								disabled={isPending}
							>
								Log in
							</StatusButton>
						</div>
					</Form>
					<div className="flex items-center justify-center gap-2 pt-6">
						<span className="text-muted-foreground">New here?</span>
						<Link
							to={
								redirectTo
									? `/signup?redirectTo=${encodeURIComponent(redirectTo)}`
									: '/signup'
							}
							className="text-link"
						>
							Create an account
						</Link>
					</div>
				</AppContainerGroupColumn>
			</AppContainerGroup>
		</AuthAppLayout>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Login to Choros' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
